import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Standardized empty state. Use anywhere a list/page has "no data yet".
 *
 * Props:
 *   icon              JSX node (typically an SVG ~48px). Optional.
 *   title             Required. e.g. "No candidates yet"
 *   description       Optional. e.g. "Invite your first candidate ..."
 *   primaryAction     Optional. { label, onClick } OR { label, to }
 *   secondaryAction   Optional. Same shape as primaryAction.
 *   compact           When true, renders smaller (for inline / panel use).
 *
 * Why this exists: the audit found ad-hoc empty markup across pages with
 * inconsistent spacing, copy, and styling. Driving everyone through this
 * primitive standardises the look + the affordances.
 */
export default function EmptyState({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  compact = false,
}) {
  const navigate = useNavigate();
  const padY = compact ? "py-8" : "py-16";
  const iconSize = compact ? "w-10 h-10" : "w-14 h-14";
  const titleSize = compact ? "text-sm" : "text-base";

  const handleAction = (a) => {
    if (!a) return;
    if (a.onClick) a.onClick();
    else if (a.to) navigate(a.to);
  };

  return (
    <div
      role="status"
      className={`flex flex-col items-center text-center ${padY} px-6`}
    >
      {icon && (
        <div
          className={`${iconSize} rounded-full bg-vf-tint text-vf-muted flex items-center justify-center mb-3`}
          aria-hidden="true"
        >
          {icon}
        </div>
      )}
      <div className={`${titleSize} font-semibold text-vf-base`}>{title}</div>
      {description && (
        <p className="text-xs text-vf-muted mt-1.5 max-w-md leading-relaxed">
          {description}
        </p>
      )}
      {(primaryAction || secondaryAction) && (
        <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
          {primaryAction && (
            <button
              type="button"
              onClick={() => handleAction(primaryAction)}
              className="px-3.5 py-1.5 rounded-md bg-vf-primary text-white text-xs font-semibold hover:opacity-90 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
            >
              {primaryAction.label}
            </button>
          )}
          {secondaryAction && (
            <button
              type="button"
              onClick={() => handleAction(secondaryAction)}
              className="px-3.5 py-1.5 rounded-md border border-subtle text-vf-base text-xs font-medium hover:bg-vf-tint transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
